import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Card, Col, Container, Row, Button, ToggleButtonGroup, ToggleButton, Badge, Alert } from "react-bootstrap";
import { FiCreditCard, FiDollarSign, FiCheck, FiStar, FiSettings, FiUsers, FiZap, FiAlertTriangle } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { createCheckoutSession, createCustomerPortalSession, resumeSubscription, startFreeSubscription, UpdateItems, updateSubscription } from "../../../api/payment";
import { useOrgContext } from "../../../context/org";
import { IPlan } from "../../../models/plan";
import { EnterprisePlan, SubscriptionPlans } from "../../../utils/plans";
import { useMutation, useQueryClient } from "react-query";
import UpgradeSubscription from "./UpgradeSubscription";
import CancelSubscription from "./CancelSubscription";
import LimitsExceeded from "./LimitsExceeded";
import SpinnerFullScreen from "../../Other/SpinnerFullScreen";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

function Subscription() {
    const [period, setPeriod] = useState<"Monthly" | "Yearly">("Monthly");
    const [currentPlan, setCurrentPlan] = useState<IPlan>();
    const [cancel, setCancel] = useState(false);
    const [limitsExceededError, setLimitsExceededError] = useState(false);
    const [scheduledPlan, setscheduledPlan] = useState<IPlan>();

    const navigate = useNavigate();
    const [errorMST, setErrorMST] = useState("");

    const [isLoading, setIsLoading] = useState(false);
    const [preview, setPreview] = useState<UpdateItems>();
    const stripeFreePriceId = SubscriptionPlans[0].monthly.priceId;

    const closeUpgradePreview = () => setPreview(undefined);
    const closeCancel = () => setCancel(false);

    const { orgId } = useParams();
    const { getOrgById, setOrg, subscription } = useOrgContext();
    const org = getOrgById(orgId || "");
    // const navigate = useNavigate();

    const queryClient = useQueryClient();
    const checkoutSessionMutation = useMutation(createCheckoutSession, {
        onSuccess: async (data) => {
            if (!data || !data.sessionId) {
                return;
            }
            await openStripeSession(data.sessionId);
        },
        onError: (error) => {
            // console.error("Error creating checkout session:", error);
        },
    });
    const updateMutation = useMutation(updateSubscription, {
        onSuccess: async (data) => {
            if (data.sessionId) {
                await openStripeSession(data.sessionId);
            } else if(data.previewOnly) setPreview(data);
            else {
                setPreview(undefined);
            }

            queryClient.invalidateQueries(["Subscriptions", orgId]);
        },
        onError: (error: any) => {
            // console.log(error);
            if (error?.response?.data?.name === "LimitsExceededError") {
                setLimitsExceededError(true);
            }
        },
    });
    const resumeMutation = useMutation(resumeSubscription, {
        onSuccess: async (data) => {
            if (data.sessionId) {
                await openStripeSession(data.sessionId);
            }
            queryClient.invalidateQueries(["Subscriptions", orgId]);
        },
        onError: (error) => {
            setErrorMST("Unable to resume subscription. Please try again later.");
        },
    });
    const startFreePlanMutation = useMutation(startFreeSubscription, {
        onSuccess: () => {
            queryClient.invalidateQueries(["Subscriptions", orgId]);
            queryClient.invalidateQueries(["Organizations"]);
        },
        onError: (error: any) => {
            if (
                error?.response?.data?.name === "ExistingFreeSubscriptionError"
            ) {
                setErrorMST(error.response.data.message);
            } else {
                setErrorMST(
                    "Unexpected error. Please try again or contact admin.",
                );
            }
        },
    });
    const openCustomerPortal = async () => {
        if (!subscription || !orgId) {
            return;
        }
        try {
            const response = await createCustomerPortalSession({
                orgId,
                subscriptionId: subscription.id,
            });

            const { url } = response;
            if (url) {
                window.location.href = url; // Redirect to the Customer Portal
            }
        } catch (error) {
            console.error("Error opening customer portal:", error);
            alert("Unable to open customer portal. Please try again.");
        }
    };
    const openStripeSession = async (sessionId: string) => {
        // console.log(data);
        const stripe = await stripePromise;
        if (!stripe) {
            console.error("Stripe failed to load.");
            return;
        }
        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) console.error(error);
    };
    const handleSubscribe = async (plan: IPlan) => {
        if (!orgId) {
            return;
        }

        const priceId = period === "Monthly" ? plan.monthly.priceId : plan.yearly.priceId;

        // changing an existing subscription
        if (subscription) {
            return updateMutation.mutate({
                orgId,
                subscriptionId: subscription.id,
                newPriceId: priceId,
                previewOnly: true,
            });
        }

        if(plan.name === "Free") {
            return startFreePlanMutation.mutate({
                orgId,
                priceId: stripeFreePriceId,
            });
        }

        checkoutSessionMutation.mutate({
            orgId,
            priceId,
        });
    };
    const handleUpgrade = async () => {
        if (preview) {
            updateMutation.mutate({
                orgId: preview.ordId,
                subscriptionId: preview.subscriptionId,
                newPriceId: preview.newPriceId,
                proration_date: preview.proration_date,
                previewOnly: false,
            });
        } else {
            setErrorMST("No upgrade preview available.");
        }
    }

    useEffect(() => {
        if (subscription) {
            const plan = SubscriptionPlans.find(
                (plan) =>
                    plan.monthly.priceId === subscription.stripePriceId ||
                    plan.yearly.priceId === subscription.stripePriceId,
            );
            if (plan) {
                setCurrentPlan(plan);
            }

            if(subscription.stripeScheduleId && subscription.schedulePriceId ) {
                const scheduledPlan = SubscriptionPlans.find(
                    (plan) => plan.monthly.priceId === subscription.schedulePriceId ||
                    plan.yearly.priceId === subscription.schedulePriceId,
                );
                setscheduledPlan(scheduledPlan);
            } else {
                setscheduledPlan(undefined);
            }
        }
    }, [subscription]);
    useEffect(() => {
        setOrg(org);
    }, [org, setOrg]);
    useEffect(() => {
        setIsLoading(
            updateMutation.isLoading ||
            resumeMutation.isLoading ||
            startFreePlanMutation.isLoading ||
            checkoutSessionMutation.isLoading
        );
    }, [
        updateMutation.isLoading,
        resumeMutation.isLoading,
        startFreePlanMutation.isLoading,
        checkoutSessionMutation.isLoading,
    ]);

    return (
        <Container
            className="py-5"
            style={{ maxWidth: "1100px", position: "relative" }}>
            <div className="text-center mb-5">
                <h1 className="text-white fw-bold mb-3">
                    <FiCreditCard className="me-3" size={32} />
                    Subscription Plans
                </h1>
                <p className="text-light lead">
                    Choose the plan that best fits your organization's needs
                </p>
            </div>

            {/* show if limits have been exceeded */}
            <LimitsExceeded
                orgId={org}
                show={limitsExceededError}
                setShow={setLimitsExceededError}
            />

            {isLoading && <SpinnerFullScreen />}
            <UpgradeSubscription
                show={!!preview}
                onClose={closeUpgradePreview}
                preview={preview}
                onUpgrade={handleUpgrade}
            />
            {/* <PauseSubscription show={pause} onClose={closePause} subscription={subscription}/> */}
            <CancelSubscription
                show={cancel}
                onClose={closeCancel}
                subscription={subscription}
            />

            {currentPlan && subscription && (
                <Card className="mb-5 bg-dark text-white border-secondary">
                    <Card.Header className="bg-dark border-secondary py-4">
                        <Card.Title
                            as="h4"
                            className="text-white fw-bold d-flex align-items-center mb-0">
                            <FiCreditCard className="me-3" size={24} />
                            Current Subscription
                        </Card.Title>
                    </Card.Header>
                    <Card.Body className="p-4">
                        <div className="d-flex align-items-center gap-3 mb-4">
                            <h3 className="text-white fw-bold mb-0">
                                {subscription.name}
                            </h3>
                            <Badge
                                bg={
                                    subscription.status === "active"
                                        ? "success"
                                        : subscription.status === "pause"
                                          ? "warning"
                                          : "danger"
                                }
                                className="px-3 py-2 fs-6">
                                {subscription.status}
                            </Badge>
                        </div>
                        <Row className="g-4">
                            <Col md={6}>
                                <div className="mb-4">
                                    <h6 className="text-secondary fw-semibold mb-2">
                                        <FiDollarSign
                                            className="me-2"
                                            size={16}
                                        />
                                        Subscription Start
                                    </h6>
                                    <p className="text-white fw-semibold mb-0">
                                        {new Date(
                                            subscription.startAt,
                                        ).toDateString()}
                                    </p>
                                </div>
                                <div className="mb-4">
                                    <h6 className="text-secondary fw-semibold mb-2">
                                        <FiSettings
                                            className="me-2"
                                            size={16}
                                        />
                                        Subscription ID
                                    </h6>
                                    <p className="text-white fw-semibold mb-0 font-monospace small">
                                        {subscription.stripeSubscriptionId}
                                    </p>
                                </div>
                                <div className="mb-4">
                                    <h6 className="text-secondary fw-semibold mb-2">
                                        <FiZap className="me-2" size={16} />
                                        Billing Cycle
                                    </h6>
                                    <p className="text-white fw-semibold mb-0">
                                        {subscription.stripePriceId ===
                                        currentPlan.monthly.priceId
                                            ? "Monthly"
                                            : "Yearly"}
                                    </p>
                                </div>
                            </Col>
                            {subscription.status === "active" && (
                                <Col md={6}>
                                    <div className="mb-4">
                                        <h6 className="text-secondary fw-semibold mb-2">
                                            <FiUsers
                                                className="me-2"
                                                size={16}
                                            />
                                            Next Billing Date
                                        </h6>
                                        <p className="text-white fw-semibold mb-0">
                                            {new Date(
                                                subscription.endAt,
                                            ).toDateString()}
                                        </p>
                                    </div>
                                    <div className="mb-4">
                                        <h6 className="text-secondary fw-semibold mb-2">
                                            <FiDollarSign
                                                className="me-2"
                                                size={16}
                                            />
                                            Next Billing Amount
                                        </h6>
                                        <p className="text-white fw-semibold mb-0">
                                            $
                                            {subscription.stripePriceId ===
                                            currentPlan.monthly.priceId
                                                ? currentPlan.monthly.price
                                                : currentPlan.yearly.price}
                                        </p>
                                    </div>
                                    <div className="mb-4">
                                        <h6 className="text-secondary fw-semibold mb-2">
                                            <FiCreditCard className="me-2" size={16}/>
                                            Payment Method
                                        </h6>
                                        <p className="text-white fw-semibold mb-2">
                                            Card
                                        </p>
                                        <Button
                                            size="sm"
                                            className="mb-2"
                                            variant="outline-light"
                                            onClick={openCustomerPortal}>
                                            <FiSettings className="me-2" size={14} />
                                            Update Payment Details
                                        </Button>
                                        <p className="text-secondary small">
                                            Payment details stored securely via
                                            Stripe
                                        </p>
                                    </div>
                                </Col>
                            )}
                        </Row>
                         <div className="d-flex gap-2">
                            {scheduledPlan && (
                                <Alert variant="info" className="w-100">
                                    <FiAlertTriangle className="me-2" />
                                    Your subscription will change to <strong>{scheduledPlan.name}</strong> at the end of this billing cycle 
                                    and you will be charged <strong>${subscription.schedulePriceId === scheduledPlan.monthly.priceId ? scheduledPlan.monthly.price : scheduledPlan.yearly.price}</strong>. 
                                    <br/>If you wish to continue with your current plan, you can click <strong>Resume</strong> below.
                                </Alert>
                            )}
                         </div>
                        <div className="d-flex gap-2">


                            {subscription.status === "active" && subscription.name !== "Free" && (
                                <Button
                                    onClick={() => setCancel(true)}
                                    variant="outline-danger">
                                    Cancel Subscription
                                </Button>
                            ) }
                            {scheduledPlan && (
                                <Button
                                    onClick={() =>
                                        resumeMutation.mutate({
                                            subscriptionId: subscription.id,
                                            orgId: subscription.organizationId,
                                        })
                                    }
                                    variant="outline-secondary">
                                    Resume Subscription
                                </Button>
                            )}
                        </div>
                    </Card.Body>
                </Card>
            )}

            <div className="text-center mb-5">
                <div className="mb-4">
                    {subscription ? (
                        <>
                            <h2 className="text-white fw-bold mb-3">
                                <FiZap className="me-3" size={28} />
                                Upgrade Your Plan
                            </h2>
                            <p className="text-light lead mb-0">
                                Easily upgrade by selecting one of the plans below
                            </p>
                        </>
                    ) : (
                        <>
                            <h2 className="text-white fw-bold mb-3">
                                <FiStar className="me-3" size={28} />
                                Choose Your Plan
                            </h2>
                            <p className="text-light lead mb-0">
                                Select the right plan for your business needs
                            </p>
                        </>
                    )}
                </div>

                <Card className="bg-dark border-secondary d-inline-block">
                    <Card.Body className="p-2">
                        <ToggleButtonGroup
                            type="radio"
                            name="billing-period"
                            value={period === "Monthly" ? "monthly" : "yearly"}
                            className="d-flex"
                        >
                            <ToggleButton
                                id="monthly"
                                variant={period === "Monthly" ? "primary" : "outline-secondary"}
                                onClick={() => setPeriod("Monthly")}
                                value="monthly"
                                className="px-4 py-2 fw-semibold"
                            >
                                Monthly
                            </ToggleButton>
                            <ToggleButton
                                id="yearly"
                                variant={period === "Yearly" ? "primary" : "outline-secondary"}
                                onClick={() => setPeriod("Yearly")}
                                value="yearly"
                                className="px-4 py-2 fw-semibold position-relative"
                            >
                                Yearly
                                <Badge bg="success" className="position-absolute top-0 start-100 translate-middle px-2 py-1 small">
                                    Save 20%
                                </Badge>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Card.Body>
                </Card>

                {errorMST && (
                    <Alert variant="danger" className="mt-4 mx-auto" style={{maxWidth: "500px"}}>
                        <FiAlertTriangle className="me-2" />
                        {errorMST}
                    </Alert>
                )}
            </div>
            <Row className="g-4 justify-content-center">
                {SubscriptionPlans.map((plan, index) => {
                    const priceId = period === "Monthly" ? plan.monthly.priceId : plan.yearly.priceId;
                    const isCurrentPlan = priceId === subscription?.stripePriceId;
                    const isProPlan = plan.name === "Pro";
                    
                    return (
                        <Col key={index} lg={3} md={6}>
                            <Card 
                                className={`h-100 position-relative transition-all ${
                                    isCurrentPlan 
                                        ? "bg-success bg-opacity-25 border-success shadow-lg" 
                                        : isProPlan 
                                            ? "bg-primary bg-opacity-25 border-primary shadow-lg scale-105" 
                                            : "bg-dark border-secondary"
                                } ${isProPlan ? "recommended-plan" : ""}`}
                                style={{ 
                                    transform: isProPlan ? "scale(1.05)" : "scale(1)",
                                    transition: "all 0.3s ease"
                                }}
                            >
                                {isProPlan && (
                                    <div className="position-absolute top-0 start-50 translate-middle">
                                        <Badge bg="primary" className="px-3 py-2 fs-6 fw-bold">
                                            <FiStar className="me-1" size={14} />
                                            MOST POPULAR
                                        </Badge>
                                    </div>
                                )}
                                
                                {isCurrentPlan && (
                                    <div className="position-absolute top-0 end-0 mt-3 me-3">
                                        <Badge bg="success" className="px-2 py-1 fs-6 fw-semibold">
                                            <FiCheck className="me-1" size={12} />
                                            Current
                                        </Badge>
                                    </div>
                                )}

                                <Card.Header 
                                    className={`text-center border-0 pt-4 ${
                                        isCurrentPlan 
                                            ? "bg-transparent" 
                                            : isProPlan 
                                                ? "bg-transparent" 
                                                : "bg-dark"
                                    }`}
                                >
                                    <div className="mb-3">
                                        <div className={`mx-auto rounded-circle d-flex align-items-center justify-content-center ${
                                            isCurrentPlan 
                                                ? "bg-success" 
                                                : isProPlan 
                                                    ? "bg-primary" 
                                                    : "bg-secondary"
                                        }`} style={{width: "60px", height: "60px"}}>
                                            {plan.name === "Free" ? (
                                                <FiUsers className="text-white" size={24} />
                                            ) : plan.name === "Pro" ? (
                                                <FiStar className="text-white" size={24} />
                                            ) : (
                                                <FiZap className="text-white" size={24} />
                                            )}
                                        </div>
                                    </div>
                                    
                                    <h4 className="text-white fw-bold mb-2">{plan.name}</h4>
                                    
                                    <div className="mb-3">
                                        <h1 className="text-white fw-bold mb-0">
                                            <span className="fs-2">$</span>
                                            <span className="display-5">
                                                {period === "Monthly" ? plan.monthly.price : plan.yearly.price}
                                            </span>
                                        </h1>
                                        <p className="mb-0" style={{color: "#ccc"}}>
                                            {index === 0 ? "Free Trial" : `per ${period.toLowerCase().slice(0, -2)}`}
                                        </p>
                                        {plan.discount && (
                                            <small className="text-success fw-semibold">
                                                {plan.discount}
                                            </small>
                                        )}
                                    </div>
                                </Card.Header>

                                <Card.Body className="d-flex flex-column p-4">
                                    <ul className="list-unstyled flex-grow-1 mb-4">
                                        {plan.features.map((feature, i) => (
                                            <li key={i} className="mb-3 d-flex align-items-start">
                                                <div className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${
                                                    isCurrentPlan 
                                                        ? "bg-success" 
                                                        : isProPlan 
                                                            ? "bg-primary" 
                                                            : "bg-secondary"
                                                }`} style={{width: "20px", height: "20px", minWidth: "20px"}}>
                                                    <FiCheck className="text-white" size={12} />
                                                </div>
                                                <span className="text-light">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    
                                    <Button
                                        className="w-100 py-3 fw-semibold"
                                        variant={
                                            isCurrentPlan 
                                                ? "outline-success" 
                                                : isProPlan 
                                                    ? "primary" 
                                                    : "outline-primary"
                                        }
                                        size="lg"
                                        disabled={isCurrentPlan}
                                        onClick={() => handleSubscribe(plan)}
                                    >
                                        {isCurrentPlan ? (
                                            <>
                                                <FiCheck className="me-2" size={16} />
                                                Current Plan
                                            </>
                                        ) : (
                                            <>
                                                {plan.name === "Free" ? (
                                                    <FiUsers className="me-2" size={16} />
                                                ) : (
                                                    <FiZap className="me-2" size={16} />
                                                )}
                                                {plan.buttonText}
                                            </>
                                        )}
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
            <div className="mt-5">
                <Card className="bg-dark border-warning shadow-lg">
                    <Card.Header className="bg-dark border-warning text-center py-4">
                        <div className="mb-3">
                            <div className="mx-auto rounded-circle bg-warning d-flex align-items-center justify-content-center" 
                                 style={{width: "80px", height: "80px"}}>
                                <FiSettings className="text-dark" size={32} />
                            </div>
                        </div>
                        <h3 className="text-white fw-bold mb-2">Enterprise Plan</h3>
                        <p className="mb-0" style={{color: '#ccc'}}>Tailored solutions for large organizations</p>
                    </Card.Header>
                    
                    <Card.Body className="p-5">
                        <Row className="align-items-center">
                            <Col lg={4} className="text-center mb-4 mb-lg-0">
                                <div className="mb-4">
                                    <h2 className="text-white fw-bold display-6 mb-2">Custom</h2>
                                    <h3 className="text-white fw-bold mb-3">Pricing</h3>
                                    <p className="text-light mb-4">
                                        Flexible pricing based on your specific requirements and usage volume
                                    </p>
                                    <Button
                                        variant="warning"
                                        size="lg"
                                        className="px-5 py-3 fw-bold text-dark"
                                        onClick={() => navigate("/dashboard/support")}
                                    >
                                        <FiUsers className="me-2" size={18} />
                                        Contact Sales Team
                                    </Button>
                                </div>
                            </Col>
                            
                            <Col lg={4} className="mb-4 mb-lg-0">
                                <h5 className="text-white fw-bold mb-3">
                                    <FiZap className="me-2 text-warning" size={20} />
                                    Advanced Features
                                </h5>
                                <ul className="list-unstyled">
                                    {EnterprisePlan.additionalFeatures?.map((feature, i) => (
                                        <li key={i} className="mb-3 d-flex align-items-start">
                                            <div className="rounded-circle bg-warning d-flex align-items-center justify-content-center me-3" 
                                                 style={{width: "20px", height: "20px", minWidth: "20px"}}>
                                                <FiCheck className="text-dark" size={12} />
                                            </div>
                                            <span className="text-light">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Col>
                            
                            <Col lg={4}>
                                <h5 className="text-white fw-bold mb-3">
                                    <FiStar className="me-2 text-warning" size={20} />
                                    Core Benefits
                                </h5>
                                <ul className="list-unstyled">
                                    {EnterprisePlan.features.map((feature, i) => (
                                        <li key={i} className="mb-3 d-flex align-items-start">
                                            <div className="rounded-circle bg-warning d-flex align-items-center justify-content-center me-3" 
                                                 style={{width: "20px", height: "20px", minWidth: "20px"}}>
                                                <FiCheck className="text-dark" size={12} />
                                            </div>
                                            <span className="text-light">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Col>
                        </Row>
                        
                        <div className="mt-4 pt-4 border-top border-secondary">
                            <Row className="text-center">
                                <Col md={4} className="mb-3">
                                    <div className="bg-warning bg-opacity-25 rounded p-3">
                                        <FiUsers className="text-warning mb-2" size={24} />
                                        <h6 className="text-white fw-semibold mb-1">Dedicated Support</h6>
                                        <small style={{color: '#ccc'}}>24/7 priority assistance</small>
                                    </div>
                                </Col>
                                <Col md={4} className="mb-3">
                                    <div className="bg-warning bg-opacity-25 rounded p-3">
                                        <FiSettings className="text-warning mb-2" size={24} />
                                        <h6 className="text-white fw-semibold mb-1">Custom Integration</h6>
                                        <small style={{color: '#ccc'}}>Tailored to your workflow</small>
                                    </div>
                                </Col>
                                <Col md={4} className="mb-3">
                                    <div className="bg-warning bg-opacity-25 rounded p-3">
                                        <FiZap className="text-warning mb-2" size={24} />
                                        <h6 className="text-white fw-semibold mb-1">Scalable Solution</h6>
                                        <small style={{color: '#ccc'}}>Grows with your business</small>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        </Container>
    );
}

export default Subscription;
